import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { UserCog, Building2 } from 'lucide-react';
import { PageLayout } from '../../components/ui/PageLayout';
import {
  BusinessRoleView,
  BusinessRoleType,
  BusinessRoleStatus,
  BusinessRoleStatsView,
  BusinessRoleListQuery,
  getBusinessRoles,
  getBusinessRoleStats,
  archiveBusinessRole,
} from '../../api/General/businessRoles';
import RoleEditModal from './components/RoleEditModal';
import RoleAssignmentPanel from './components/RoleAssignmentPanel';
import BulkAssignmentModal from './components/BulkAssignmentModal';
import RevisionHistoryPanel from './components/RevisionHistoryPanel';
import ClarityMeter from '@/components/clarity/ClarityMeter';
import ManagedElsewhereNotice from '../../components/capabilities/ManagedElsewhereNotice';
import { useCompanyCapabilities } from '../../hooks/useCompanyCapabilities';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import Pagination from '@/components/Pagination';

const roleTypeLabels: Record<BusinessRoleType, string> = {
  [BusinessRoleType.Department]: 'Department',
  [BusinessRoleType.Project]: 'Project',
  [BusinessRoleType.Temporary]: 'Temporary',
};

const roleTypeStyles: Record<BusinessRoleType, string> = {
  [BusinessRoleType.Department]: 'bg-primary/10 text-primary border-primary/20',
  [BusinessRoleType.Project]: 'bg-purple-100 text-purple-700 border-purple-200',
  [BusinessRoleType.Temporary]: 'bg-orange-100 text-orange-700 border-orange-200',
};

// Clarity chip color: null → neutral, >=80 green, >=50 amber, else red.
const clarityColor = (score?: number): string => {
  if (score == null) return 'bg-muted text-muted-foreground';
  if (score >= 80) return 'bg-success/10 text-success-strong';
  if (score >= 50) return 'bg-warning/10 text-warning';
  return 'bg-destructive/10 text-destructive';
};

const statusLabels: Record<BusinessRoleStatus, string> = {
  [BusinessRoleStatus.Active]: 'Active',
  [BusinessRoleStatus.Inactive]: 'Inactive',
  [BusinessRoleStatus.Draft]: 'Draft',
};

const statusStyles: Record<BusinessRoleStatus, string> = {
  [BusinessRoleStatus.Active]: 'bg-success/10 text-success-strong',
  [BusinessRoleStatus.Inactive]: 'bg-muted text-muted-foreground',
  [BusinessRoleStatus.Draft]: 'bg-warning/10 text-warning',
};

type FilterTab = 'all' | 'active' | 'inactive' | 'unassigned' | 'dueToRevision' | 'needsClarity';

// A denied call rejects with the parsed Result body, whose status carries the 403.
const readStatus = (error: unknown): number | undefined =>
  (error as { status?: number } | null)?.status
  ?? (error as { response?: { status?: number } } | null)?.response?.status;

export default function TeamRoleManagement() {
  const { t } = useTranslation();

  const [roles, setRoles] = useState<BusinessRoleView[]>([]);
  const [stats, setStats] = useState<BusinessRoleStatsView | null>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Filters
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortDescending, setSortDescending] = useState(false);

  // Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<BusinessRoleView | null>(null);

  // Details panel
  const [detailsRole, setDetailsRole] = useState<BusinessRoleView | null>(null);
  const detailsRef = useRef<HTMLDivElement>(null);

  // Bulk assignment modal
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);

  // The server refuses the list for a manager who has no department of their own.
  const [forbidden, setForbidden] = useState(false);

  // Gated on `loaded` so authoring buttons never appear and then vanish for a managed company.
  const { can, loaded: capabilitiesLoaded } = useCompanyCapabilities();
  const canAuthor = capabilitiesLoaded && can('canAuthorRoles');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const query: BusinessRoleListQuery = {
        search: search || undefined,
        type: typeFilter !== 'all' ? typeFilter as BusinessRoleType : undefined,
        tag: tagFilter !== 'all' ? tagFilter : undefined,
        filterTab: filterTab !== 'all' ? filterTab : undefined,
        sortBy,
        sortDescending,
        page,
        pageSize,
      };

      // allSettled, not all: the list call 403s for a manager who has no department,
      // and that must not discard a perfectly good stats response.
      const [listOutcome, statsOutcome] = await Promise.allSettled([
        getBusinessRoles(query),
        getBusinessRoleStats(),
      ]);

      if (listOutcome.status === 'fulfilled' && listOutcome.value.errors.length === 0) {
        setRoles(listOutcome.value.payload.items);
        setTotalCount(listOutcome.value.payload.totalCount);
        setTotalPages(listOutcome.value.payload.totalPages);
        setAvailableTags(listOutcome.value.payload.availableTags ?? []);
        setForbidden(false);
      } else {
        // A 403 REJECTS rather than resolving, so the rejection is the branch that fires;
        // the resolved check is belt-and-braces. Mirrors SkillRoleGapsSection.
        const status = listOutcome.status === 'rejected'
          ? readStatus(listOutcome.reason)
          : listOutcome.value.status;
        if (status === 403) {
          setForbidden(true);
          setRoles([]);
          setTotalCount(0);
          setTotalPages(1);
          setDetailsRole(null);
        }
      }

      if (statsOutcome.status === 'fulfilled' && statsOutcome.value.errors.length === 0) {
        setStats(statsOutcome.value.payload);
      }
    } catch (error) {
      // allSettled cannot reject, so this only catches a malformed 200; log it rather than
      // letting it escape as an unhandled rejection from an un-awaited caller.
      console.error('Failed to load business roles', error);
    } finally {
      setLoading(false);
    }
  }, [search, filterTab, typeFilter, tagFilter, sortBy, sortDescending, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    const valid: FilterTab[] = ['all', 'active', 'inactive', 'unassigned', 'dueToRevision', 'needsClarity'];
    if (tab && (valid as string[]).includes(tab)) {
      setFilterTab(tab as FilterTab);
      setPage(1);
    }
    // run once on mount to honor a ?tab= deep-link from the Q1 drill-down
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?role=<id> opens that role's details panel, which then scrolls itself into view. Deliberately
  // separate from the ?tab= effect above: this one has to wait for `roles` to arrive, and it must
  // only fire for the id currently in the URL — re-running it on every load would reopen a panel
  // the user had closed. Equipment Coverage links here from a role's requirement group.
  const deepLinkedRoleId = searchParams.get('role');
  const consumedRoleDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkedRoleId || consumedRoleDeepLink.current === deepLinkedRoleId) return;
    const match = roles.find((r) => String(r.id) === deepLinkedRoleId);
    if (!match) return; // still loading, or on another page of results
    consumedRoleDeepLink.current = deepLinkedRoleId;
    setDetailsRole(match);
  }, [deepLinkedRoleId, roles]);

  // The details panel renders below a full page of rows, so opening one otherwise looks like nothing happened.
  useEffect(() => {
    if (detailsRole) detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [detailsRole]);

  const handleFilterTabChange = (tab: FilterTab) => {
    setFilterTab(tab);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleCreateRole = () => {
    if (!canAuthor) return;
    setSelectedRole(null);
    setEditModalOpen(true);
  };

  const handleEditRole = (role: BusinessRoleView) => {
    if (!canAuthor) return;
    setSelectedRole(role);
    setEditModalOpen(true);
  };

  const handleOpenBulkAssign = () => {
    if (!canAuthor) return;
    setBulkAssignModalOpen(true);
  };

  const handleDeleteRole = async (role: BusinessRoleView) => {
    if (!canAuthor) return;
    if (!window.confirm(t('businessRoles.confirmDelete', { name: role.name }))) return;
    try {
      const result = await archiveBusinessRole(role.id);
      if (result.errors.length === 0) {
        loadData();
        if (detailsRole?.id === role.id) setDetailsRole(null);
      }
    } catch {
      // silent
    }
  };

  const handleRoleSaved = () => {
    loadData();
  };

  const filterTabs: { key: FilterTab; label: string; count: number | undefined; tooltip?: string }[] = [
    { key: 'all', label: t('businessRoles.filterAll'), count: stats?.total },
    { key: 'unassigned', label: t('businessRoles.filterUnassigned'), count: stats?.unassigned },
    { key: 'dueToRevision', label: t('businessRoles.filterDueToRevision'), count: stats?.dueToRevision },
    { key: 'needsClarity', label: t('businessRoles.filterNeedsClarity'), count: stats?.needsClarity, tooltip: t('businessRoles.filterNeedsClarityTooltip') },
    { key: 'active', label: t('businessRoles.filterActive'), count: stats?.active },
    { key: 'inactive', label: t('businessRoles.filterInactive'), count: stats?.inactive },
  ];

  const filterTabStyles: Record<FilterTab, string> = {
    all: 'text-foreground border-border',
    unassigned: 'text-warning border-warning',
    dueToRevision: 'text-orange-700 border-orange-400',
    needsClarity: 'text-amber-700 border-amber-400',
    active: 'text-success border-success',
    inactive: 'text-muted-foreground border-border',
  };

  // A manager with no department of their own cannot list roles, so the whole screen
  // explains that instead of showing an empty grid and a create button that leads nowhere.
  if (forbidden) {
    return (
      <PageLayout
        title={t('businessRoles.pageTitle')}
        description={t('businessRoles.pageSubtitle')}
        icon={UserCog}
        accentColor="amber"
        maxWidth="1600px"
      >
        <Card variant="elevated" data-testid="roles-no-department">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-lg font-medium text-foreground mb-2">{t('businessRoles.noScopeTitle')}</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">{t('businessRoles.noScopeBody')}</p>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('businessRoles.pageTitle')}
      description={t('businessRoles.pageSubtitle')}
      icon={UserCog}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        canAuthor ? (
          <Button
            variant="outline"
            onClick={handleCreateRole}
            disabled={!stats}
            iconName="Plus"
            data-testid="create-role-button"
          >
            {t('businessRoles.createRole')}
          </Button>
        ) : undefined
      }
    >
      <ManagedElsewhereNotice capability="canAuthorRoles" className="mb-6" />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('businessRoles.totalRoles')}</p>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('businessRoles.activeRoles')}</p>
                  <p className="text-2xl font-bold text-success">{stats.active}</p>
                </div>
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('businessRoles.draftRoles')}</p>
                  <p className="text-2xl font-bold text-warning">{stats.draft}</p>
                </div>
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('businessRoles.inactiveRoles')}</p>
                  <p className="text-2xl font-bold text-muted-foreground">{stats.inactive}</p>
                </div>
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="elevated">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('businessRoles.dueToRevision')}</p>
                  <p className="text-2xl font-bold text-warning">{stats.dueToRevision}</p>
                </div>
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Roles Pool Header + Filter Tabs */}
      <Card variant="elevated" className="mb-6">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-foreground">{t('businessRoles.rolesPool')}</h2>
            <div className="relative">
              <svg className="w-4 h-4 absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('businessRoles.searchPlaceholder')}
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="ps-10 pe-4 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary w-72 text-sm"
                data-testid="roles-search"
              />
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-border overflow-x-auto">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleFilterTabChange(tab.key)}
              title={tab.tooltip}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors min-h-[44px] ${
                filterTab === tab.key
                  ? `${filterTabStyles[tab.key]} bg-opacity-10 border`
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`filter-tab-${tab.key}`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs ${
                  filterTab === tab.key ? 'bg-current bg-opacity-20' : 'bg-muted text-muted-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}

          <div className="ms-auto flex items-center gap-2">
            {availableTags.length > 0 && (
              <select
                value={tagFilter}
                onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
                aria-label={t('a11y.filterByTag')}
                className="border border-border rounded-lg px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="tag-filter"
              >
                <option value="all">{t('businessRoles.allTags')}</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            )}
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              aria-label={t('a11y.filterByType')}
              className="border border-border rounded-lg px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              data-testid="type-filter"
            >
              <option value="all">{t('businessRoles.allTypes')}</option>
              {Object.entries(roleTypeLabels).map(([val, label]) => (
                <option key={val} value={val}>{t(`businessRoles.type${label}`)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Roles Grid / List */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-12 h-12 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h3 className="text-lg font-medium text-foreground mb-2">{t('businessRoles.noRolesFound')}</h3>
              {canAuthor && (
                <>
                  <p className="text-muted-foreground mb-4">{t('businessRoles.noRolesDescription')}</p>
                  <Button
                    onClick={handleCreateRole}
                    iconName="Plus"
                  >
                    {t('businessRoles.createFirstRole')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map((role) => (
                  <Card
                    key={role.id}
                    variant="interactive"
                    className={`p-4 ${
                      detailsRole?.id === role.id ? 'border-primary ring-2 ring-ring' : ''
                    }`}
                    onClick={() => setDetailsRole(detailsRole?.id === role.id ? null : role)}
                    data-testid={`role-card-${role.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground truncate">{role.name}</h3>
                        {role.category && (
                          <p className="text-xs text-muted-foreground mt-0.5">{role.category}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 ms-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${roleTypeStyles[role.type]}`}>
                          {t(`businessRoles.type${roleTypeLabels[role.type]}`)}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[role.status]}`}>
                          {t(`businessRoles.status${statusLabels[role.status]}`)}
                        </span>
                      </div>
                    </div>

                    {role.description && (
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{role.description}</p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          {role.assignedCount}/{role.numberOfSeats}
                        </span>
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${clarityColor(role.clarityScore)}`}
                          title={t('businessRoles.clarityTooltip')}
                          data-testid={`role-clarity-${role.id}`}
                        >
                          {role.clarityScore == null ? '—' : `${role.clarityScore}%`}
                        </span>
                        {role.requiredSkills.length > 0 && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            {role.requiredSkills.length} {t('businessRoles.skills')}
                          </span>
                        )}
                      </div>
                      {canAuthor && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="min-h-[44px] min-w-[44px]"
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleEditRole(role); }}
                            aria-label={t('a11y.editRole', { name: role.name })}
                            data-testid={`edit-role-${role.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="min-h-[44px] min-w-[44px] hover:text-destructive hover:bg-destructive/5"
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleDeleteRole(role); }}
                            aria-label={t('a11y.deleteRole', { name: role.name })}
                            data-testid={`delete-role-${role.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </Button>
                        </div>
                      )}
                    </div>

                    {role.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-border">
                        {role.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="inline-flex px-1.5 py-0.5 bg-muted text-muted-foreground rounded text-xs">{tag}</span>
                        ))}
                        {role.tags.length > 3 && (
                          <span className="text-xs text-muted-foreground">+{role.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 pt-4 border-t border-border">
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={(p) => setPage(p)}
                        variant="compact"
                    />
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Role Details Panel */}
      {detailsRole && (
        <Card variant="elevated" ref={detailsRef}>
          <CardContent>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{detailsRole.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${roleTypeStyles[detailsRole.type]}`}>
                    {t(`businessRoles.type${roleTypeLabels[detailsRole.type]}`)}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[detailsRole.status]}`}>
                    {t(`businessRoles.status${statusLabels[detailsRole.status]}`)}
                  </span>
                  {detailsRole.category && (
                    <span className="text-xs text-muted-foreground">{detailsRole.category}</span>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDetailsRole(null)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>

            {detailsRole.description && (
              <p className="text-sm text-muted-foreground mb-4">{detailsRole.description}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Core Responsibilities */}
              {detailsRole.coreResponsibilities.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">{t('businessRoles.coreResponsibilities')}</h3>
                  <ul className="space-y-1">
                    {detailsRole.coreResponsibilities.map((resp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="text-primary/60 mt-1">{'\u2022'}</span>
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Decision Rights */}
              {detailsRole.decisionRights.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">{t('businessRoles.decisionRights')}</h3>
                  <ul className="space-y-1">
                    {detailsRole.decisionRights.map((dr, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="text-primary/60 mt-1">{'\u2022'}</span>
                        {dr}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Required Skills */}
              {detailsRole.requiredSkills.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">{t('businessRoles.requiredSkills')}</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {detailsRole.requiredSkills.map((skill, i) => (
                      <span key={i} className="inline-flex px-2 py-0.5 bg-primary/5 text-primary rounded-md text-xs">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Seats info. The clarity chip used to sit here, beside a seat count it has nothing
                  to do with; it now has its own section below, with the breakdown behind it. */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">{t('businessRoles.capacity')}</h3>
                <p className="text-sm text-muted-foreground">
                  {detailsRole.assignedCount} / {detailsRole.numberOfSeats} {t('businessRoles.seatsUsed')}
                </p>
              </div>

              {/* Success Criteria */}
              {detailsRole.successCriteria?.description && (
                <div className="md:col-span-2">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{t('businessRoles.successCriteria')}</h3>
                  <p className="text-sm text-muted-foreground">{detailsRole.successCriteria.description}</p>
                  {detailsRole.successCriteria.nextReviewDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('businessRoles.nextReview')}: {new Date(detailsRole.successCriteria.nextReviewDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Clarity. Read-only by design: the score is recalculated server-side on every save, so
                the only way to change it is to change the role. A second "analyze" control here would
                produce a number the next save could disagree with. */}
            <div className="mt-6">
              <ClarityMeter
                clarity={detailsRole.clarity}
                score={detailsRole.clarityScore}
                updatedOn={detailsRole.clarityScoreUpdatedOn}
                headingKey="businessRoles.clarity"
                hintKey="businessRoles.clarityHint"
                emptyKey="businessRoles.clarityEmpty"
                howScoredKey="businessRoles.clarityHowScored"
                feedsKey="businessRoles.clarityFeedsQ1"
                fieldLabelKey="businessRoles.clarityField"
                data-testid="role-clarity-panel"
                testIdPrefix="clarity"
              />
            </div>

            {/* Bulk Assign Button */}
            {canAuthor && (
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenBulkAssign}
                  iconName="Users"
                >
                  {t('businessRoles.bulkAssign')}
                </Button>
              </div>
            )}

            {/* Role Assignments. Keyed so switching roles remounts: the panel's recommend
                state is per-role and would otherwise describe the previously opened one. */}
            <RoleAssignmentPanel
              key={detailsRole.id}
              role={detailsRole}
              onAssignmentChanged={loadData}
            />

            {/* Revision History */}
            <RevisionHistoryPanel roleId={detailsRole.id} />
          </CardContent>
        </Card>
      )}

      {/* Edit/Create Modal */}
      {/* Scope is unknown until stats resolve; treat that as scoped so the field never falls open. */}
      <RoleEditModal
        role={selectedRole}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSaved={handleRoleSaved}
        isCompanyScope={stats?.isCompanyScope ?? false}
        scopeDepartmentId={stats?.scopeDepartmentId}
        scopeDepartmentName={stats?.scopeDepartmentName}
      />

      {/* Bulk Assignment Modal */}
      {detailsRole && (
        <BulkAssignmentModal
          role={detailsRole}
          open={bulkAssignModalOpen}
          onClose={() => setBulkAssignModalOpen(false)}
          onCompleted={loadData}
        />
      )}
    </PageLayout>
  );
}
